import {
  Button,
  HStack,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
} from "@chakra-ui/react";
import React, { useEffect, useState, useCallback } from "react";
import { DayPicker, Matcher, TZDate, OnSelectHandler } from "react-day-picker";
import {
  DatePickerPresetsDropdown,
  DatePreset,
} from "./DatePickerPresetsDropdown";
import { DatePickerTimeFields } from "./DatePickerTimeFields";
import { DayPickerChevron } from "./DayPickerChevron";
import { DayPickerDropdown } from "./DayPickerDropdown";
import {
  fromTZDate,
  toTZDate,
  startOfMinuteInTimeZone,
  middayInTimeZone,
} from "./tzDateUtils";

type DatePickerPopoverContentProps = {
  value: Date | undefined;
  earliestDate?: Date;
  latestDate?: Date;
  showTime: boolean;
  presetTitle?: string;
  presetDates?: DatePreset[];

  /**
   * The Date in the popover has changed, but the user may still be interacting
   * with the date picker.
   */
  onChange: (date: Date | undefined) => void;

  /**
   * A finalised Date and Time has been selected (the selection is finalised,
   * and no longer in progress, so the popover can be closed).
   */
  onPresetSelect: (date: Date | undefined) => void;

  /**
   * Close with no selection
   */
  onClose: () => void;

  timeZone: string;
};

export const DatePickerPopoverContent = React.forwardRef<
  HTMLDivElement,
  DatePickerPopoverContentProps
>(
  (
    {
      value,
      earliestDate,
      latestDate,
      showTime,
      presetTitle,
      presetDates,
      onChange,
      onPresetSelect,
      onClose,
      timeZone,
    },
    ref,
  ) => {
    const pickerEarliestDate =
      earliestDate ? toTZDate(earliestDate, timeZone) : undefined;

    const pickerLatestDate =
      latestDate ? toTZDate(latestDate, timeZone) : undefined;

    const disabledDays: Matcher[] = [
      ...(pickerEarliestDate ? [{ before: pickerEarliestDate }] : []),
      ...(pickerLatestDate ? [{ after: pickerLatestDate }] : []),
    ];

    const [visibleMonth, setVisibleMonth] = useState<Date>(
      value ? toTZDate(value, timeZone) : TZDate.tz(timeZone),
    );

    // re-compute the visible month whenever the value is externally updated.
    useEffect(() => {
      const month = value ? toTZDate(value, timeZone) : TZDate.tz(timeZone);
      setVisibleMonth(month);
    }, [value, timeZone]);

    const handleDayPickerSelect: OnSelectHandler<Date | undefined> =
      useCallback(
        (
          date: Date | undefined, // this is a TZDate
        ) => {
          if (!date) {
            onChange(undefined);
            return;
          }

          if (showTime) {
            // default to midday time if a date wasn't previously selected.
            if (!value) {
              const nextDate = fromTZDate(date);
              const midday = middayInTimeZone(nextDate, timeZone);
              onChange(midday);
              return;
            }

            // apply the time components from the previous date to the new one
            const currentTzDate = toTZDate(value, timeZone);
            const withTime = new TZDate(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              currentTzDate.getHours(),
              currentTzDate.getMinutes(),
              currentTzDate.getSeconds(),
              currentTzDate.getMilliseconds(),
              currentTzDate.timeZone,
            );
            const nextDate = fromTZDate(withTime);
            onChange(nextDate);
            return;
          }

          const nextDate = fromTZDate(date);
          onChange(nextDate);
        },
        [showTime, onChange, value, timeZone],
      );

    const closeOnSelect = !showTime;

    return (
      <PopoverContent
        zIndex="dropdown"
        ref={ref}
        w="auto"
        maxW="100vw"
        bg="white"
      >
        <PopoverBody>
          <DayPicker
            mode="single"
            month={visibleMonth}
            animate={true}
            timeZone={timeZone}
            onMonthChange={setVisibleMonth}
            selected={value ? toTZDate(value, timeZone) : undefined}
            onSelect={handleDayPickerSelect}
            disabled={disabledDays}
            captionLayout="dropdown"
            navLayout="after"
            startMonth={pickerEarliestDate}
            endMonth={pickerLatestDate}
            reverseYears
            fixedWeeks
            showOutsideDays
            components={{
              Chevron: DayPickerChevron,
              Dropdown: DayPickerDropdown,
            }}
          />

          {showTime && (
            <DatePickerTimeFields
              value={value}
              onChange={onChange}
              timeZone={timeZone}
            />
          )}
        </PopoverBody>

        <PopoverFooter>
          <HStack justify="space-between">
            {presetDates && presetDates.length > 0 && (
              <DatePickerPresetsDropdown
                value={value}
                title={presetTitle}
                presets={presetDates}
                onSelect={onChange}
              />
            )}

            {showTime && (
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  const now = startOfMinuteInTimeZone(new Date(), timeZone);
                  onPresetSelect(now);
                }}
              >
                Now
              </Button>
            )}

            {/* it's important to have a cancel/ close button, as the popper may end up covering the screen on small devices. */}
            <HStack>
              {value && (
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => onChange(undefined)}
                >
                  Clear
                </Button>
              )}

              {closeOnSelect && (
                <Button
                  size="sm"
                  variant="ghost"
                  colorScheme="gray"
                  onClick={onClose}
                >
                  Close
                </Button>
              )}

              {!closeOnSelect && (
                <Button
                  size="sm"
                  variant={value ? "solid" : "outline"}
                  colorScheme={value ? "primary" : "gray"}
                  onClick={onClose}
                >
                  {value ? "Done" : "Close"}
                </Button>
              )}
            </HStack>
          </HStack>
        </PopoverFooter>
      </PopoverContent>
    );
  },
);
